// Time-zone-aware helpers for the registration start/end `datetime-local` inputs.
//
// A `datetime-local` value is a bare wall-clock string ("YYYY-MM-DDTHH:mm") with
// no zone. The admin picks an explicit IANA zone for the registration, so we must
// interpret that wall-clock in the *selected* zone (not the browser's) when
// converting to a stored UTC instant, and convert back the same way when
// hydrating the input. dayjs (already a dep) + its utc/timezone plugins do this
// via the platform Intl data — no extra tz database shipped.
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

/** The viewer's current IANA zone, used as the default when none is selected. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Stored instant (Date/ISO/ms) → "YYYY-MM-DDTHH:mm" wall-clock in `timeZone`,
 * for populating a `<input type="datetime-local">`. Falls back to the browser
 * zone when `timeZone` is empty (preserves legacy behavior).
 */
export function toDatetimeLocalInZone(
  value: Date | string | number | null | undefined,
  timeZone?: string | null,
): string {
  if (!value) return '';
  const zone = timeZone || browserTimeZone();
  const d = dayjs(value as any).tz(zone);
  return d.isValid() ? d.format('YYYY-MM-DDTHH:mm') : '';
}

/**
 * "YYYY-MM-DDTHH:mm" wall-clock (as typed) interpreted in `timeZone` → ISO UTC
 * instant for storage, or null when empty/invalid. Falls back to the browser
 * zone when `timeZone` is empty.
 */
export function fromDatetimeLocalInZone(
  localString: string | null | undefined,
  timeZone?: string | null,
): string | null {
  if (!localString) return null;
  const zone = timeZone || browserTimeZone();
  const d = dayjs.tz(localString, zone);
  return d.isValid() ? d.toISOString() : null;
}
